import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { hasPermission, type Permission, type SessionUser } from '@pd/shared';
import { api, ApiError, setUnauthorizedHandler } from '../api/client';
import { openChannel, TAB_ID, type TabMessage } from './tabSync';

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  can: (permission: Permission) => boolean;
  setUser: (user: SessionUser | null) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);
const ME_KEY = ['auth', 'me'];

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ME_KEY,
    queryFn: () =>
      api.get<SessionUser>('/auth/me').catch((e) => {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }),
    staleTime: Infinity,
    // Coming back to a tab re-checks the session: it may have been ended on another device.
    refetchOnWindowFocus: 'always',
    retry: false,
  });

  const channel = useRef<BroadcastChannel | null>(null);
  const announce = useCallback((userId: string | null) => {
    channel.current?.postMessage({ type: 'auth', userId } satisfies TabMessage);
  }, []);

  /** Sets the signed-in user (sign-in, profile change) and tells the other tabs. */
  const setUser = useCallback(
    (u: SessionUser | null) => {
      qc.setQueryData(ME_KEY, u);
      announce(u?.id ?? null);
    },
    [qc, announce],
  );

  /**
   * Signs the user out locally: first mark "no user" (route guards send them to the
   * sign-in page), then drop every other cached query. `qc.clear()` must not be used here:
   * it detaches the mounted "me" query, which keeps showing the old user.
   */
  const clearLocal = useCallback(() => {
    qc.setQueryData(ME_KEY, null);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
  }, [qc]);

  /** Signs out here and in every other tab of this browser (they share the cookie). */
  const dropSession = useCallback(() => {
    clearLocal();
    announce(null);
  }, [clearLocal, announce]);

  useEffect(() => {
    // Any 401 from the API (expired / revoked session) drops the user back to login.
    setUnauthorizedHandler(dropSession);
  }, [dropSession]);

  // Follow the other tabs, and answer their "who is open?" pings.
  useEffect(() => {
    const ch = openChannel((msg) => {
      if (msg.type === 'ping' && msg.from !== TAB_ID) {
        ch?.postMessage({ type: 'pong', from: TAB_ID, to: msg.from } satisfies TabMessage);
        return;
      }
      if (msg.type !== 'auth') return;
      const current = qc.getQueryData<SessionUser | null>(ME_KEY);
      if (msg.userId === null) {
        if (current) clearLocal();
        return;
      }
      // Someone else signed in here: nothing cached for the previous user may remain.
      if (current?.id !== msg.userId) clearLocal();
      void qc.invalidateQueries({ queryKey: ME_KEY });
    });
    channel.current = ch;
    return () => {
      ch?.close();
      channel.current = null;
    };
  }, [qc, clearLocal]);

  const value = useMemo<AuthState>(() => {
    const user = me.data ?? null;
    return {
      user,
      loading: me.isPending,
      can: (p) => !!user && hasPermission(user.role, p),
      setUser,
      logout: async () => {
        await api.post('/auth/logout').catch(() => undefined);
        dropSession();
      },
    };
  }, [me.data, me.isPending, setUser, dropSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
